import React from "react";
import { ConnectedUserAvatar, USER_AVATAR_SIZE } from "./UserAvatar";
import { ImageWithFallback } from "App/common/ImageWithFallback";
import { useTheme } from "App/common/ThemeProvider";
import { ReactComponent as AvatarSVG } from "App/common/icons-v2/avatar.svg";
import "./UserAvatarGroup.scss";

const DEFAULT_MAX_AVATARS = 3;

export type AvatarGroupUser = {
  id?: Id;
  alias?: string;
  /**
   * When set, render this URL directly instead of fetching the user by id —
   * for callers that already have the picture (e.g. the pre-join "join to view"
   * preview, where a non-member can't read the member list to resolve ids).
   */
  avatarURL?: string;
};

export const UserAvatarGroup = ({
  users,
  total,
  size,
  showMinimalVersion,
  maxAvatars = DEFAULT_MAX_AVATARS,
}: {
  users?: AvatarGroupUser[];
  /**
   * Total member count when `users` is only a sample of a larger set (drives
   * the "+N"). Falls back to the number of (deduped) `users`.
   *
   * Under the paginated roster read, `users` is one page of the roster
   * rather than the whole roster, so without an explicit total the "+N"
   * and the minimal variant's count silently under-report on any space with
   * more members than the page size.
   */
  total?: number;
  size?: USER_AVATAR_SIZE;
  showMinimalVersion?: boolean;
  /** How many avatars to show before collapsing the rest into "+N". */
  maxAvatars?: number;
}) => {
  const { theme } = useTheme();

  const seen: Record<string, boolean> = {};
  const dedupedUsers = (users || []).filter((user) => {
    const key = user.id ?? user.avatarURL;
    if (!key || seen[key]) return false;
    seen[key] = true;
    return true;
  });
  const count = total ?? dedupedUsers.length;

  if (showMinimalVersion) {
    return (
      <div className="user-avatar-group-minimal">
        <span className="user-avatar-group-minimal-icon">
          <AvatarSVG />
        </span>
        {count}
      </div>
    );
  }

  const shownUsers = dedupedUsers.slice(0, maxAvatars);
  const overflow = count - shownUsers.length;

  return (
    <div className="user-avatar-group">
      <div className="avatars">
        {shownUsers.map(({ id, alias, avatarURL }, i) =>
          avatarURL ? (
            <div
              key={id ?? `${avatarURL}-${i}`}
              className="highnote-user-avatar"
              data-size={size || USER_AVATAR_SIZE.MEDIUM}
              data-theme={theme}
            >
              <ImageWithFallback src={avatarURL} fallback={<AvatarSVG />} />
            </div>
          ) : (
            <ConnectedUserAvatar
              key={id}
              userId={id}
              alias={alias}
              size={size || USER_AVATAR_SIZE.MEDIUM}
            />
          ),
        )}
      </div>
      {overflow > 0 && <span className="overflow">+{overflow}</span>}
    </div>
  );
};
